import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { userAPI } from '../../services/api';
import { FiUser, FiLogOut, FiArrowRight, FiBell, FiEdit2, FiCamera, FiChevronRight, FiCheck, FiX } from 'react-icons/fi';
import LogoWithArrow from "../common/LogoWithArrow";
import NotificationPanel from './NotificationPanel';
import api from '../../services/api';
import { toast } from 'react-toastify';


const Navbar = () => {
  const { user, logout, isAuthenticated, fetchUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.scrollY > 20;
  });
  const [showProfileSheet, setShowProfileSheet] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const sheetRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 20;
      if (scrolled !== isScrolled) setIsScrolled(scrolled);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  // Close panels on outside tap
  useEffect(() => {
    if (!showProfileSheet && !showNotifications) return;
    const handleClickOutside = (e) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target)) {
        setShowProfileSheet(false);
      }
      // Notification panel handled by its own backdrop on mobile, 
      // but for desktop dropdown we might want a similar check if it wasn't for the backdrop.
      // However, for simplicity and cross-platform consistency, we'll use state toggles.
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showProfileSheet, showNotifications]);

  // Fetch notifications for workers
  const fetchNotifications = async () => {
    if (!isAuthenticated || user?.role !== 'worker') return;
    try {
      setNotificationsLoading(true);
      const response = await api.get('/user/notifications');
      if (response.data.success) {
        setNotifications(response.data.data);
        const unread = response.data.data.filter(n => n.status !== 'read').length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.role === 'worker') {
      fetchNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, user?.role]);

  const toggleNotifications = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (user?.role !== 'worker') {
      // For users/admins, maybe navigate to their dashboard or just ignore
      // But requirement says "If logged-in user role === 'worker'"
      // or "If user is NOT logged in: Redirect to login"
      // No specific requirement for 'user' role but let's just navigate to dashboard for now
      // unless we want to allow it for everyone.
      // Requirement says: "Worker should see notifications directly"
      navigate(getDashboardLink());
      return;
    }
    setShowNotifications(!showNotifications);
  };

  const markNotificationAsRead = async (id) => {
    try {
      await api.put(`/user/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n._id === id ? { ...n, status: 'read' } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to update notification');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/user/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
      setUnreadCount(0);
      toast.success('All marked as read');
    } catch (error) {
      toast.error('Failed to update notifications');
    }
  };


  // Lock body scroll when sheet open
  useEffect(() => {
    if (showProfileSheet) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showProfileSheet]);

  // Clear upload states when sheet closes
  useEffect(() => {
    if (!showProfileSheet) {
      setUploadSuccess(false);
      setUploadError('');
    }
  }, [showProfileSheet]);

  const handleLogout = () => {
    setShowProfileSheet(false);
    logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'admin': return '/admin-dashboard/dashboard';
      case 'worker': return '/worker/dashboard';
      default: return '/user/dashboard';
    }
  };

  const handleAvatarTap = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setShowProfileSheet(true);
  };

  const handleSheetOption = (action) => {
    if (action === 'picture') {
      // Trigger file picker
      fileInputRef.current?.click();
      return; // Don't close sheet yet
    }
    setShowProfileSheet(false);
    switch (action) {
      case 'view':
        navigate(getDashboardLink());
        break;
      case 'edit':
        navigate(getDashboardLink());
        break;
      default:
        break;
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Only JPG, PNG, and WEBP images are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be under 5MB.');
      return;
    }

    setUploading(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      await userAPI.updateProfileImage(file);
      // Refresh user data so avatar updates everywhere
      await fetchUser();
      setUploadSuccess(true);
      setUploading(false);

      // Auto-close sheet after success
      setTimeout(() => {
        setShowProfileSheet(false);
      }, 1500);
    } catch (err) {
      console.error('Profile image upload failed:', err);
      setUploadError(err.response?.data?.message || 'Upload failed. Please try again.');
      setUploading(false);
    }

    // Reset file input so same file can be selected again
    e.target.value = '';
  };

  // Profile avatar renderer
  const renderAvatar = (size = 36) => {
    if (isAuthenticated && user?.profileImage) {
      return (
        <img
          src={user.profileImage}
          alt={user.name || 'Profile'}
          className="rounded-full object-cover border-2 border-white/20"
          style={{ width: size, height: size, transition: 'all 0.3s ease' }}
        />
      );
    }

    if (isAuthenticated && user?.name) {
      return (
        <div
          className="rounded-full flex items-center justify-center font-bold text-white"
          style={{
            width: size,
            height: size,
            background: '#6A5ACD',
            fontSize: size * 0.4,
            transition: 'all 0.3s ease',
          }}
        >
          {user.name.charAt(0).toUpperCase()}
        </div>
      );
    }

    // Default — not authenticated
    return (
      <div
        className="rounded-full flex items-center justify-center bg-white/10"
        style={{ width: size, height: size, transition: 'all 0.3s ease' }}
      >
        <FiUser className="text-white/70" style={{ width: size * 0.5, height: size * 0.5 }} />
      </div>
    );
  };

  const navLinks = [
    { name: 'Find Workers', path: '/workers' },
    { name: 'Contact', path: '/contact' },
  ];

  const isWorkersPage = location.pathname === '/workers';
  const isAuthPage = ['/login', '/register', '/otp-login'].some(path => location.pathname.startsWith(path));

  // Desktop navbar background style
  let navbarClasses = '';
  if (isAuthPage) {
    navbarClasses = 'bg-white/90 backdrop-blur-sm py-4 border-b border-slate-100 shadow-sm';
  } else if (isScrolled) {
    navbarClasses = 'bg-white/80 backdrop-blur-xl py-3 shadow-[0_8px_32px_rgba(0,0,0,0.08)] border-b border-white/20';
  } else {
    // Both Landing and Workers pages will use the same transparent/glassy start
    navbarClasses = 'bg-white/40 backdrop-blur-md py-5 border-b border-white/10';
  }


  const sheetOptions = [
    { key: 'view', icon: FiUser, label: 'View Profile', color: '#1A1D26' },
    { key: 'edit', icon: FiEdit2, label: 'Edit Profile', color: '#1A1D26' },
    { key: 'picture', icon: FiCamera, label: 'Change Profile Picture', color: '#6A5ACD' },
  ];

  return (
    <>
      {/* Hidden file input for profile image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* ===== MOBILE APP HEADER — Premium SaaS Sticky Glass Style ===== */}
      <nav
        className={`fixed top-0 left-0 right-0 z-[5000] lg:hidden transition-all duration-300
          bg-burgundy-900/80 backdrop-blur-md border-b border-white/10
          ${isScrolled ? 'shadow-[0_8px_30px_rgb(0,0,0,0.3)]' : 'shadow-none'}`}
      >
        <div className="flex items-center justify-between px-5 h-14">
          {/* Logo */}
          <Link to="/" aria-label="NammaEventz Home" className="active:scale-95 transition-transform">
            <LogoWithArrow
              text="NammaEventz"
              fontSize={20}
              color="#FFFFFF"
              fontWeight="800"
              letterSpacing="0.02em"
              gradient={true}
            />
          </Link>

          {/* Right: Bell + Avatar */}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleNotifications}
              className="relative w-9 h-9 rounded-xl flex items-center justify-center text-white/90 active:bg-white/10 transition-colors"
              aria-label="Notifications"
            >
              <FiBell className="w-[20px] h-[20px]" />
              {/* Dynamic unread dot */}
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2.5 w-2 h-2 bg-rose-500 rounded-full border border-indigo-950 shadow-sm"></span>
              )}
            </button>
            <button
              onClick={handleAvatarTap}
              className="active:scale-90 transition-transform flex-shrink-0"
              aria-label="Profile"
            >
              <div className="rounded-full border border-white/20 p-[2px]">
                {renderAvatar(32)}
              </div>
            </button>
          </div>

          <NotificationPanel
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
            notifications={notifications}
            loading={notificationsLoading}
            onMarkAsRead={markNotificationAsRead}
            onMarkAllRead={markAllAsRead}
            unreadCount={unreadCount}
          />

        </div>
      </nav>

      {/* ===== PROFILE BOTTOM SHEET MODAL ===== */}
      {showProfileSheet && (
        <div className="fixed inset-0 z-[6000] lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          />

          {/* Sheet */}
          <div
            ref={sheetRef}
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl"
            style={{
              animation: 'slideUp 0.3s ease-out',
              paddingBottom: 'env(safe-area-inset-bottom, 16px)',
            }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-[#E5E7EB]" />
            </div>

            {/* Profile header in sheet */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#F3F4F6]">
              <div className="relative">
                {renderAvatar(48)}
                {uploading && (
                  <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[16px] font-bold text-[#1A1D26] truncate">
                  {user?.name || 'User'}
                </h3>
                <p className="text-[12px] text-[#9CA3AF] truncate">
                  {user?.email || 'user@example.com'}
                </p>
              </div>
            </div>

            {/* Upload status messages */}
            {uploadSuccess && (
              <div className="mx-5 mt-3 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-green-50 border border-green-100">
                <FiCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-[13px] font-medium text-green-700">Profile picture updated!</span>
              </div>
            )}
            {uploadError && (
              <div className="mx-5 mt-3 flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-100">
                <FiX className="w-4 h-4 text-red-500 flex-shrink-0" />
                <span className="text-[13px] font-medium text-red-600">{uploadError}</span>
              </div>
            )}

            {/* Options */}
            <div className="py-2">
              {sheetOptions.map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => handleSheetOption(opt.key)}
                  disabled={uploading}
                  className="flex items-center gap-3.5 w-full px-5 py-3.5 active:bg-[#F6F7FB] transition-colors disabled:opacity-50"
                >
                  <div className="w-9 h-9 rounded-xl bg-[#F6F7FB] flex items-center justify-center">
                    <opt.icon className="w-[18px] h-[18px]" style={{ color: opt.color }} />
                  </div>
                  <span className="flex-1 text-left text-[14px] font-medium text-[#1A1D26]">
                    {opt.key === 'picture' && uploading ? 'Uploading...' : opt.label}
                  </span>
                  <FiChevronRight className="w-4 h-4 text-[#D1D5DB]" />
                </button>
              ))}

              {/* Logout — separate with divider */}
              <div className="mx-5 my-1 border-t border-[#F3F4F6]" />
              <button
                onClick={handleLogout}
                className="flex items-center gap-3.5 w-full px-5 py-3.5 active:bg-red-50 transition-colors"
              >
                <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
                  <FiLogOut className="w-[18px] h-[18px] text-red-500" />
                </div>
                <span className="flex-1 text-left text-[14px] font-medium text-red-500">
                  Logout
                </span>
              </button>
            </div>

            {/* Bottom padding */}
            <div className="h-2" />
          </div>
        </div>
      )}

      {/* ===== CSS Animations ===== */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>

      {/* ===== DESKTOP NAVBAR — unchanged ===== */}
      <nav
        className={`fixed top-0 z-[5000] w-full transition-[background-color,padding,box-shadow,border-color] duration-500 ease-out ${navbarClasses} hidden lg:block ${isWorkersPage ? 'hidden lg:block' : ''}`}
      >
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          <Link to="/" className="relative z-[1001] block transition-transform duration-300 hover:scale-105" aria-label="NammaEventz Home">
            <LogoWithArrow text="NammaEventz" fontSize={32} color="#E63946" />
          </Link>

          <div className="flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-sm font-bold transition-all duration-300 text-[#1A2B48] hover:text-cherry-500 relative group tracking-wide"
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-cherry-500 transition-all duration-300 group-hover:w-full"></span>
              </Link>
            ))}

            {isAuthenticated ? (
              <div className="flex items-center gap-6 pl-8 border-l border-slate-200/50">
                <div className="relative group/notif">
                  <button
                    onClick={toggleNotifications}
                    className="p-2.5 rounded-full text-[#1A2B48] hover:text-cherry-600 hover:bg-cherry-50 transition-all duration-300 relative"
                    title="Notifications"
                  >
                    <FiBell size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full border border-white ring-2 ring-rose-50 shadow-sm animate-pulse"></span>
                    )}
                  </button>

                  <NotificationPanel
                    isOpen={showNotifications}
                    onClose={() => setShowNotifications(false)}
                    notifications={notifications}
                    loading={notificationsLoading}
                    onMarkAsRead={markNotificationAsRead}
                    onMarkAllRead={markAllAsRead}
                    unreadCount={unreadCount}
                  />
                </div>

                <Link
                  to={getDashboardLink()}
                  className="text-sm font-bold transition-all duration-300 text-[#1A2B48] hover:text-cherry-500 tracking-wide"
                >
                  Dashboard
                </Link>
                <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-[#1A2B48]/5 border border-[#1A2B48]/10 group hover:bg-[#1A2B48]/10 transition-colors">
                  <FiUser className="text-[#1A2B48]" />
                  <span className="text-xs font-black uppercase tracking-widest text-[#1A2B48]">
                    {user?.name?.split(' ')[0]}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2.5 rounded-full text-slate-400 hover:text-cherry-600 hover:bg-cherry-50 transition-all duration-300"
                  title="Logout"
                >
                  <FiLogOut size={18} />
                </button>
              </div>
            ) : (

              <div className="flex items-center gap-6 ml-4">
                <Link
                  to="/login"
                  className="text-sm font-bold text-[#1A2B48] hover:text-cherry-500 transition-colors px-4 py-2"
                >
                  Login
                </Link>
                <Link to="/register">
                  <button className="bg-gradient-to-r from-cherry-500 to-cherry-600 hover:shadow-[0_15px_35px_rgba(230,57,70,0.3)] text-white text-xs font-black uppercase tracking-[0.2em] px-8 py-3.5 rounded-xl transition-all duration-500 transform hover:-translate-y-1 active:scale-95 flex items-center gap-2 shadow-[0_10px_25px_rgba(230,57,70,0.2)]">
                    Get Started
                    <FiArrowRight size={16} />
                  </button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
